import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { LinkedFields } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import InlineLoader from "@/components/ui/inline-loader";
import {
  ADD_LINKED_FIELD_TRANSLATION_KEY,
  ENTITY_TYPE_OPTIONS
} from "../const";
import { FieldOption } from "./linkedFieldUtils";

export interface CreateFormSectionsProps {
  selectedObjectType: EntityType | null;
  selectedFieldId: string | null;
  fieldOptions: FieldOption[];
  existingFieldLabels: string[];
  isLoading: boolean;
  formSetValue: any;
  formatFieldLabel: (value: string | number) => string;
  t: (key: string) => string;
  entityType?: EntityType;
  onObjectTypeSelection?: (value: string | number) => void;
  onFieldIdSelection?: (value: string | number) => void;
}

export const createFormSections = ({
  selectedObjectType,
  selectedFieldId,
  fieldOptions,
  existingFieldLabels,
  isLoading,
  formatFieldLabel,
  t,
  entityType,
  onObjectTypeSelection,
  onFieldIdSelection
}: CreateFormSectionsProps): FormSectionProps[] => {
  const getEntityTypeOptions = () => {
    if (entityType === EntityType.PERSON) {
      return ENTITY_TYPE_OPTIONS.filter(
        (option) => option.id === EntityType.COMPANY
      );
    }
    if (entityType === EntityType.LEAD) {
      return ENTITY_TYPE_OPTIONS.filter(
        (option) =>
          option.id === EntityType.COMPANY || option.id === EntityType.PERSON
      );
    }
    if (entityType === EntityType.DEAL) {
      return ENTITY_TYPE_OPTIONS.filter(
        (option) => option.id !== EntityType.DEAL
      );
    }
    if (entityType === EntityType.CONTRACT) {
      return ENTITY_TYPE_OPTIONS.filter(
        (option) =>
          option.id === EntityType.COMPANY || option.id === EntityType.DEAL
      );
    }
    return ENTITY_TYPE_OPTIONS;
  };

  const entityTypeOptions = getEntityTypeOptions();
  const formattedFieldOptions = fieldOptions.map((option) => ({
    id: option.id,
    displayName: option.displayName,
    subtitle: option.iconComponent ? (
      <div className="flex items-center gap-1">
        <IconContainer size="xs" color="secondary">
          <option.iconComponent />
        </IconContainer>
        <Text variant="smBodySecondary">{option.formattedDisplayType}</Text>
      </div>
    ) : (
      <Text variant="smBodySecondary">{option.formattedDisplayType}</Text>
    ),
    displayType: option.displayType
  }));

  return [
    {
      id: "info-card",
      divider: false,
      sectionClassName: "gap-6",
      fields: [],
      bottomCustomComponent: (
        <div className="flex flex-row border gap-3 border-border-widget-card rounded-lg h-[100px] mb-6 bg-surface-elevation2">
          <Text variant="body" className="w-[295px] p-5 pr-0">
            {t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.linkedFieldDescription`)}
          </Text>
          <div className="">
            <LinkedFields />
          </div>
        </div>
      )
    },
    {
      id: "object-selection",
      divider: true,
      sectionClassName: "gap-6",
      dividerClassName: "my-2",
      fields: [
        {
          name: "objectType",
          type: "singleSelect" as const,
          label: t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.object`),
          required: true,
          fieldActionProps: {
            fieldName: "objectType",
            options: entityTypeOptions as any,
            placeholder: t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.selectObject`),
            renderIcon: (value: string | number) => {
              const option = ENTITY_TYPE_OPTIONS.find(
                (opt) => opt.id === value
              );
              if (!option) return <div />;
              return (
                <IconContainer size="sm" color="secondary">
                  <option.icon />
                </IconContainer>
              );
            },
            onSelection: onObjectTypeSelection
          }
        }
      ]
    },
    {
      id: "field-selection",
      divider: false,
      sectionClassName: "gap-6",
      fields: [
        {
          name: "fieldId",
          type: "singleSelect" as const,
          label: t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.field`),
          required: true,
          fieldActionProps: {
            fieldName: "fieldId",
            options: formattedFieldOptions,
            placeholder:
              isLoading && selectedObjectType
                ? t("loading")
                : t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.selectField`),
            disabled: !selectedObjectType || isLoading,
            showSelectedItemSubtitle: false,
            formatter: formatFieldLabel,
            onSelection: onFieldIdSelection
          }
        }
      ],
      bottomCustomComponent:
        isLoading && selectedObjectType ? (
          <div className="flex justify-center py-4">
            <InlineLoader label={t("loading")} />
          </div>
        ) : undefined
    },
    {
      id: "field-label",
      divider: false,
      sectionClassName: "gap-6",
      fields: [
        {
          name: "fieldLabel",
          type: "text" as const,
          label: t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.fieldName`),
          required: true,
          fieldActionProps: {
            placeholder: t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.addFieldName`),
            disabled: !selectedFieldId
          },
          validation: {
            customRules: [
              {
                validate: (value: string) =>
                  !value ||
                  !existingFieldLabels.includes(value.trim().toLowerCase()),
                message: t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.duplicateName`)
              }
            ]
          }
        }
      ]
    }
  ];
};
